import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { usePermissoes } from '../hooks/usePermissoes';
import { VisitasService } from '../services/visitasService';
import { ComprovanteService } from '../services/comprovanteService';
import FotoVisitaService from '../services/fotoVisitaService';
import SelfieCapture from '../components/common/SelfieCapture';
import { Visita, Usuario, VisitaSlot, StatusVisita } from '../types';
import toast from 'react-hot-toast';

const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
const diasSemanaCompleto = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];

const statusConfig: Record<StatusVisita, { label: string; cor: string; proximos: StatusVisita[] }> = {
  'PENDENTE': { label: 'Pendente', cor: 'bg-yellow-100 text-yellow-800 border-yellow-300', proximos: [] },
  'APROVADO': { label: 'Aprovado', cor: 'bg-blue-100 text-blue-800 border-blue-300', proximos: [] },
  'REPROVADO': { label: 'Reprovado', cor: 'bg-red-100 text-red-800 border-red-300', proximos: [] },
  'REAGENDADO': { label: 'Proposta de Reagendamento', cor: 'bg-orange-100 text-orange-800 border-orange-300', proximos: [] },
  'CANCELADO': { label: 'Cancelado', cor: 'bg-gray-100 text-gray-800 border-gray-300', proximos: [] },
  'CHEGOU': { label: 'Chegou', cor: 'bg-green-100 text-green-800 border-green-300', proximos: ['APROVADO'] },
  'EM_ATENDIMENTO': { label: 'Em Atendimento', cor: 'bg-indigo-100 text-indigo-800 border-indigo-300', proximos: ['CHEGOU'] },
  'FINALIZADO': { label: 'Finalizado', cor: 'bg-emerald-100 text-emerald-800 border-emerald-300', proximos: ['EM_ATENDIMENTO'] },
  'NAO_COMPARECEU': { label: 'Não Compareceu', cor: 'bg-red-100 text-red-800 border-red-300', proximos: [] }
};

const fluxogramaStatus: StatusVisita[] = ['PENDENTE', 'APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO'];

// Grade semanal: { data: { inicio: { ocupado, empresa } } }
type SlotSemana = { ocupado: boolean; pendente: boolean; status?: string; empresa?: string; data: string; inicio: string; fim: string };
type GradeSemana = Record<string, Record<string, SlotSemana>>; // data -> inicio -> slot

const AgendarVisitas: React.FC = () => {
  const { user } = useAuth();
  const { temPermissao: podeSolicitar } = usePermissoes('visitas', 'solicitar');
  const [compradores, setCompradores] = useState<Usuario[]>([]);
  const [compradorSelecionado, setCompradorSelecionado] = useState<Usuario | null>(null);
  const [dataSelecionada, setDataSelecionada] = useState<string>(new Date().toISOString().split('T')[0]);
  const [slots, setSlots] = useState<VisitaSlot[]>([]);
  const [fechado, setFechado] = useState(false);
  const [motivoFechado, setMotivoFechado] = useState('');
  const [carregando, setCarregando] = useState(false);
  const [carregandoSemana, setCarregandoSemana] = useState(false);
  const [gradeSemana, setGradeSemana] = useState<GradeSemana>({});
  const [horariosUnicos, setHorariosUnicos] = useState<string[]>([]);
  const [diasSemanaAtual, setDiasSemanaAtual] = useState<string[]>([]);
  const [erroCompradores, setErroCompradores] = useState<string>('');
  const [minhasVisitas, setMinhasVisitas] = useState<Visita[]>([]);
  const [statusFiltro, setStatusFiltro] = useState<StatusVisita[]>([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [etapaModal, setEtapaModal] = useState<'horario' | 'formulario'>('horario');
  const [slotSelecionado, setSlotSelecionado] = useState<VisitaSlot | null>(null);
  const [abaAtiva, setAbaAtiva] = useState<'agenda' | 'minhas'>('agenda');
  const [mesAtual, setMesAtual] = useState<Date>(new Date());

  const [formVisita, setFormVisita] = useState({
    assunto: '',
    documento: '',
    empresa_representada: '',
    nome_visitante: user?.nome || '',
    email_visitante: user?.email || '',
    telefone_visitante: '',
    observacoes: ''
  });
  const [fotoVisitanteFile, setFotoVisitanteFile] = useState<File | null>(null);
  const [enviandoSolicitacao, setEnviandoSolicitacao] = useState(false);

  useEffect(() => {
    carregarCompradores();
  }, []);

  useEffect(() => {
    if (compradorSelecionado) {
      carregarDisponibilidade();
      carregarGradeSemana();
    }
  }, [compradorSelecionado, dataSelecionada]);

  useEffect(() => {
    carregarMinhasVisitas();
  }, []);

  const carregarCompradores = async () => {
    const filial = user?.filiais?.[0];
    let { data, error } = await VisitasService.buscarCompradores(filial);

    // Se não encontrou nenhum comprador na filial, tenta buscar todos
    if (!error && (!data || data.length === 0) && filial) {
      const { data: todosCompradores, error: erroSemFilial } = await VisitasService.buscarCompradores();
      if (!erroSemFilial && todosCompradores && todosCompradores.length > 0) {
        data = todosCompradores;
      }
    }

    setCompradores(data);
    if (error) {
      const mensagem = error instanceof Error ? error.message : 'Erro ao carregar compradores';
      setErroCompradores(mensagem);
      toast.error(`Erro ao carregar compradores: ${mensagem}`);
    } else if (!data || data.length === 0) {
      setErroCompradores('Nenhum comprador encontrado.');
    } else {
      setErroCompradores('');
    }
  };

  const carregarDisponibilidade = async () => {
    if (!compradorSelecionado) return;
    setCarregando(true);
    const resultado = await VisitasService.buscarDisponibilidade(compradorSelecionado.id, dataSelecionada);
    setSlots(resultado.data);
    setFechado(resultado.fechado);
    setMotivoFechado(resultado.motivo || '');
    setCarregando(false);
  };

  const getInicioSemana = (dataStr: string): Date => {
    const d = new Date(dataStr + 'T12:00:00');
    const dia = d.getDay();
    const diff = dia === 0 ? -6 : 1 - dia;
    const seg = new Date(d);
    seg.setDate(d.getDate() + diff);
    return seg;
  };

  const carregarGradeSemana = async () => {
    if (!compradorSelecionado) return;
    setCarregandoSemana(true);
    const seg = getInicioSemana(dataSelecionada);
    const dias: string[] = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(seg);
      d.setDate(seg.getDate() + i);
      dias.push(d.toISOString().split('T')[0]);
    }
    setDiasSemanaAtual(dias);

    const resultados = await Promise.all(
      dias.map(d => VisitasService.buscarDisponibilidade(compradorSelecionado.id, d))
    );

    const grade: GradeSemana = {};
    const horariosSet = new Set<string>();

    dias.forEach((data, i) => {
      grade[data] = {};
      resultados[i].data.forEach(slot => {
        horariosSet.add(slot.inicio);
        grade[data][slot.inicio] = {
          ocupado: slot.ocupado || false,
          pendente: false,
          status: (slot as any).status || undefined,
          empresa: (slot as any).empresa || undefined,
          data,
          inicio: slot.inicio,
          fim: slot.fim
        };
      });
    });

    const horariosOrdenados = Array.from(horariosSet).sort();
    setHorariosUnicos(horariosOrdenados);
    setGradeSemana(grade);
    setCarregandoSemana(false);
  };

  const carregarMinhasVisitas = async () => {
    if (!user) return;
    const dados = await VisitasService.buscarVisitas({ solicitante_id: user.id });
    setMinhasVisitas(dados);
  };

  const getDiasDoMes = (mes: Date): (number | null)[] => {
    const primeiroDia = new Date(mes.getFullYear(), mes.getMonth(), 1);
    const ultimoDia = new Date(mes.getFullYear(), mes.getMonth() + 1, 0);
    const dias: (number | null)[] = [];

    // Preencher dias vazios antes do primeiro dia do mês
    for (let i = 0; i < primeiroDia.getDay(); i++) {
      dias.push(null);
    }

    // Preencher dias do mês
    for (let dia = 1; dia <= ultimoDia.getDate(); dia++) {
      dias.push(dia);
    }

    return dias;
  };

  const formatarData = (ano: number, mes: number, dia: number): string => {
    return `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
  };

  const getCorDia = (dia: number) => {
    const hoje = new Date();
    const dataDia = new Date(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const dataDiaStr = formatarData(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const diaSemana = dataDia.getDay();

    const isHoje = dataDia.toDateString() === hoje.toDateString();
    const isSelecionado = dataDiaStr === dataSelecionada;

    // Fim de semana
    if (diaSemana === 0 || diaSemana === 6) {
      return isSelecionado
        ? 'bg-red-600 text-white ring-2 ring-red-300'
        : isHoje
        ? 'bg-red-400 text-white ring-2 ring-yellow-400'
        : 'bg-red-100 text-red-700';
    }

    // Dia selecionado
    if (isSelecionado) {
      return 'bg-blue-600 text-white ring-2 ring-blue-300 font-bold';
    }

    // Hoje
    if (isHoje) {
      return 'bg-blue-100 text-blue-800 ring-2 ring-yellow-400 font-bold';
    }

    // Dia passado
    if (dataDia < new Date(hoje.getFullYear(), hoje.getMonth(), hoje.getDate())) {
      return 'bg-gray-100 text-gray-400 cursor-not-allowed';
    }

    return 'bg-white text-gray-700 hover:bg-blue-50';
  };

  const handleDiaClick = (dia: number) => {
    const dataDia = new Date(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);

    if (dataDia < hoje) return;

    const dataDiaStr = formatarData(mesAtual.getFullYear(), mesAtual.getMonth(), dia);
    setDataSelecionada(dataDiaStr);

    if (!compradorSelecionado) {
      toast('Selecione um comprador para agendar uma visita.', { icon: 'ℹ️' });
      return;
    }

    setSlotSelecionado(null);
    setEtapaModal('horario');
    setModalAberto(true);
  };

  const handleSelecionarSlot = (slot: VisitaSlot, data?: string) => {
    if (data) setDataSelecionada(data);
    setSlotSelecionado(slot);
    setEtapaModal('formulario');
    setModalAberto(true);
    setFormVisita(prev => ({ ...prev, nome_visitante: user?.nome || '', email_visitante: user?.email || '' }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!compradorSelecionado || !slotSelecionado) return;

    if (!fotoVisitanteFile) {
      toast.error('A foto do visitante (selfie) é obrigatória.');
      return;
    }

    setEnviandoSolicitacao(true);
    const toastId = toast.loading('Enviando foto...');

    const { url: fotoUrl, error: erroFoto } = await FotoVisitaService.uploadFotoVisitante(fotoVisitanteFile);
    console.log('[AgendarVisitas] Upload resultado:', { fotoUrl, erroFoto });
    if (erroFoto || !fotoUrl) {
      toast.error(erroFoto || 'Erro ao enviar a foto do visitante', { id: toastId });
      setEnviandoSolicitacao(false);
      return;
    }

    toast.loading('Enviando solicitação...', { id: toastId });

    const payloadVisita = {
      comprador_id: compradorSelecionado.id,
      data_visita: dataSelecionada,
      horario_inicio: slotSelecionado.inicio,
      horario_fim: slotSelecionado.fim,
      foto_visitante_url: fotoUrl,
      ...formVisita
    };
    console.log('[AgendarVisitas] Enviando para API:', payloadVisita);

    const { data, error } = await VisitasService.criarVisita(payloadVisita);
    console.log('[AgendarVisitas] Resposta API:', { data, error });

    setEnviandoSolicitacao(false);

    if (error) {
      toast.error(error.message || 'Erro ao solicitar visita', { id: toastId });
      return;
    }

    toast.success('Solicitação de visita enviada!', { id: toastId });
    setModalAberto(false);
    setFormVisita({
      assunto: '',
      documento: '',
      empresa_representada: '',
      nome_visitante: user?.nome || '',
      email_visitante: user?.email || '',
      telefone_visitante: '',
      observacoes: ''
    });
    setFotoVisitanteFile(null);
    carregarDisponibilidade();
    carregarMinhasVisitas();
  };

  const handleCancelarVisita = async (visita: Visita) => {
    const motivo = window.prompt('Motivo do cancelamento:');
    if (motivo === null) return;
    if (!motivo.trim()) {
      toast.error('Informe o motivo do cancelamento');
      return;
    }
    const { error } = await VisitasService.cancelarVisita(visita.id, motivo.trim());
    if (error) {
      toast.error(error.message || 'Erro ao cancelar');
      return;
    }
    toast.success('Visita cancelada');
    carregarMinhasVisitas();
  };

  const toggleStatusFiltro = (status: StatusVisita) => {
    setStatusFiltro(prev =>
      prev.includes(status) ? prev.filter(s => s !== status) : [...prev, status]
    );
  };

  const handleComprovante = async (visita: Visita) => {
    const { data, error } = await VisitasService.gerarComprovante(visita.id);
    if (error || !data) {
      toast.error(error?.message || 'Erro ao carregar comprovante');
      return;
    }
    const sucesso = await ComprovanteService.gerarComprovanteVisita(data);
    if (!sucesso) {
      toast.error('Erro ao gerar PDF do comprovante');
    }
  };

  const handleReagendarClick = (visita: Visita) => {
    toast('Para reagendar, cancele esta solicitação e crie uma nova.', { icon: 'ℹ️' });
  };

  const handleAprovar = async (visita: Visita) => {
    if (!window.confirm('Aprovar o reagendamento proposto?')) return;
    const { error } = await VisitasService.aprovarVisita(visita.id);
    if (error) {
      toast.error(error.message || 'Erro ao aprovar');
      return;
    }
    toast.success('Reagendamento aprovado!');
    carregarMinhasVisitas();
  };

  const handleReprovar = async (visita: Visita) => {
    const motivo = window.prompt('Informe o motivo da reprovação:');
    if (!motivo) return;
    const { error } = await VisitasService.reprovarVisita(visita.id, motivo);
    if (error) {
      toast.error(error.message || 'Erro ao reprovar');
      return;
    }
    toast.success('Reagendamento reprovado');
    carregarMinhasVisitas();
  };

  const renderFluxograma = (statusAtual: StatusVisita) => {
    const indexAtual = fluxogramaStatus.indexOf(statusAtual);
    return (
      <div className="flex items-center gap-1 flex-wrap">
        {fluxogramaStatus.map((status, index) => {
          const ativo = index <= indexAtual;
          const atual = status === statusAtual;
          const config = statusConfig[status];
          return (
            <React.Fragment key={status}>
              <div className={`px-2 py-1 rounded text-xs font-medium border ${ativo ? config.cor : 'bg-gray-50 text-gray-400 border-gray-200'} ${atual ? 'ring-2 ring-offset-1 ring-blue-400' : ''}`}>
                {config.label}
              </div>
              {index < fluxogramaStatus.length - 1 && (
                <div className={`w-4 h-0.5 ${index < indexAtual ? 'bg-blue-500' : 'bg-gray-200'}`} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Agendar Visitas</h1>
          <p className="text-gray-600 mt-1">Agende sua visita aos compradores de forma rápida e organizada.</p>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selecionar Comprador</label>
              <div className={`relative rounded-lg transition-all duration-300 ${compradorSelecionado ? 'ring-2 ring-blue-500 ring-offset-1' : 'ring-2 ring-orange-400 ring-offset-1 animate-pulse'}`}>
                <select
                  className="w-full rounded-lg border-2 border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 bg-white px-3 py-2.5 text-gray-900 font-medium shadow-sm cursor-pointer"
                  value={compradorSelecionado?.id || ''}
                  onChange={(e) => {
                    const comprador = compradores.find(c => c.id === e.target.value);
                    setCompradorSelecionado(comprador || null);
                  }}
                >
                  <option value="">Selecione um comprador</option>
                  {compradores.map(c => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              {erroCompradores && (
                <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {erroCompradores}
                </p>
              )}
            </div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-1">Data</label>
                <input
                  type="date"
                  className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                  value={dataSelecionada}
                  min={new Date().toISOString().split('T')[0]}
                  onChange={(e) => setDataSelecionada(e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-6 border-b border-gray-200">
          <button
            onClick={() => setAbaAtiva('agenda')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${abaAtiva === 'agenda' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
          >
            Agenda
          </button>
          <button
            onClick={() => setAbaAtiva('minhas')}
            className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${abaAtiva === 'minhas' ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-600 hover:text-gray-800'}`}
          >
            Minhas Solicitações
          </button>
        </div>

        {abaAtiva === 'agenda' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-1">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-md font-semibold text-gray-900">
                      {mesAtual.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })}
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth() - 1, 1))}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                        </svg>
                      </button>
                      <button
                        onClick={() => setMesAtual(new Date())}
                        className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                      >
                        Hoje
                      </button>
                      <button
                        onClick={() => setMesAtual(new Date(mesAtual.getFullYear(), mesAtual.getMonth() + 1, 1))}
                        className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
                      >
                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  <div className="grid grid-cols-7 gap-0.5 sm:gap-1 text-center">
                    {diasSemana.map(dia => (
                      <div key={dia} className="font-semibold text-gray-600 p-1 sm:p-2 text-xs sm:text-sm">
                        {dia}
                      </div>
                    ))}

                    {getDiasDoMes(mesAtual).map((dia, index) => {
                      if (dia === null) {
                        return <div key={`empty-${index}`} className="p-1 sm:p-2"></div>;
                      }

                      return (
                        <button
                          key={dia}
                          onClick={() => handleDiaClick(dia)}
                          className={`p-1 sm:p-2 text-xs sm:text-sm rounded-lg font-medium ${getCorDia(dia)} transition-colors min-h-[40px] sm:min-h-[48px] flex flex-col items-center justify-center`}
                        >
                          <span>{dia}</span>
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex flex-wrap items-center gap-3 mt-4 text-xs text-gray-600">
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-blue-600"></div>
                      <span>Selecionado</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-blue-100 ring-1 ring-yellow-400"></div>
                      <span>Hoje</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-red-100"></div>
                      <span>Fechado</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <div className="w-3 h-3 rounded bg-gray-100"></div>
                      <span>Passado</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                {compradorSelecionado ? (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="text-lg font-semibold text-gray-900">
                        Agenda do Comprador — Semana de {diasSemanaAtual[0] ? new Date(diasSemanaAtual[0] + 'T12:00:00').toLocaleDateString('pt-BR') : ''}
                      </h2>
                      {carregandoSemana && <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>}
                    </div>

                    {carregandoSemana ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      </div>
                    ) : horariosUnicos.length === 0 ? (
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                        <p className="text-yellow-700">Nenhum horário configurado para esta semana.</p>
                      </div>
                    ) : (
                      <>
                        {/* Desktop: tabela */}
                        <div className="hidden md:block overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="border-b border-gray-200">
                                <th className="py-2 px-3 text-left text-gray-500 font-medium w-20">Horário</th>
                                {diasSemanaAtual.map(data => {
                                  const d = new Date(data + 'T12:00:00');
                                  const isHoje = d.toDateString() === new Date().toDateString();
                                  const isSelecionado = data === dataSelecionada;
                                  return (
                                    <th key={data}
                                      className={`py-2 px-3 text-center font-medium cursor-pointer hover:bg-gray-50 ${isSelecionado ? 'text-blue-600' : isHoje ? 'text-yellow-600' : 'text-gray-700'}`}
                                      onClick={() => setDataSelecionada(data)}
                                    >
                                      <div>{['Seg','Ter','Qua','Qui','Sex'][diasSemanaAtual.indexOf(data)]}</div>
                                      <div className={`text-xs font-normal ${isSelecionado ? 'text-blue-500' : 'text-gray-400'}`}>
                                        {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                      </div>
                                    </th>
                                  );
                                })}
                              </tr>
                            </thead>
                            <tbody>
                              {horariosUnicos.map(horario => (
                                <tr key={horario} className="border-b border-gray-100 hover:bg-gray-50">
                                  <td className="py-2 px-3 text-gray-500 font-medium text-xs">{horario}</td>
                                  {diasSemanaAtual.map(data => {
                                    const slot = gradeSemana[data]?.[horario];
                                    const isSelecionado = data === dataSelecionada;
                                    if (!slot) {
                                      return (
                                        <td key={data} className={`py-2 px-3 text-center ${isSelecionado ? 'bg-blue-50' : ''}`}>
                                          <span className="text-gray-300 text-xs">—</span>
                                        </td>
                                      );
                                    }
                                    const podeClicar = podeSolicitar && !slot.ocupado;
                                    return (
                                      <td key={data} className={`py-2 px-3 text-center ${isSelecionado ? 'bg-blue-50' : ''}`}>
                                        <button
                                          onClick={() => podeClicar ? handleSelecionarSlot({ inicio: slot.inicio, fim: slot.fim, ocupado: slot.ocupado } as VisitaSlot, slot.data) : slot.ocupado ? toast('Horário já ocupado.', { icon: 'ℹ️' }) : null}
                                          disabled={!podeClicar}
                                          className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors ${
                                            slot.ocupado
                                              ? slot.status === 'PENDENTE'
                                                ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                                                : 'bg-red-100 text-red-700 cursor-not-allowed'
                                              : podeClicar
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer'
                                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                          }`}
                                        >
                                          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${slot.ocupado ? (slot.status === 'PENDENTE' ? 'bg-yellow-500' : 'bg-red-500') : 'bg-green-500'}`}></span>
                                          {slot.ocupado ? (slot.status === 'PENDENTE' ? 'Em análise' : slot.empresa || 'Agendado') : 'Livre'}
                                        </button>
                                      </td>
                                    );
                                  })}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>

                        {/* Mobile: cards por dia */}
                        <div className="md:hidden space-y-3">
                          {diasSemanaAtual.map(data => {
                            const d = new Date(data + 'T12:00:00');
                            const isSelecionado = data === dataSelecionada;
                            const isHoje = d.toDateString() === new Date().toDateString();
                            const diaLabel = ['Seg','Ter','Qua','Qui','Sex'][diasSemanaAtual.indexOf(data)];
                            return (
                              <div key={data} className={`rounded-lg border p-3 ${isSelecionado ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'}`}>
                                <button onClick={() => setDataSelecionada(data)} className="w-full flex items-center justify-between mb-2">
                                  <span className={`font-semibold ${isSelecionado ? 'text-blue-700' : isHoje ? 'text-yellow-600' : 'text-gray-800'}`}>
                                    {diaLabel}, {d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                                  </span>
                                  <span className="text-xs text-gray-400">{isSelecionado ? 'selecionado' : 'tocar para selecionar'}</span>
                                </button>
                                <div className="grid grid-cols-2 gap-2">
                                  {horariosUnicos.map(horario => {
                                    const slot = gradeSemana[data]?.[horario];
                                    if (!slot) return null;
                                    const podeClicar = podeSolicitar && !slot.ocupado;
                                    return (
                                      <button
                                        key={horario}
                                        onClick={() => podeClicar ? handleSelecionarSlot({ inicio: slot.inicio, fim: slot.fim, ocupado: slot.ocupado } as VisitaSlot, slot.data) : slot.ocupado ? toast('Horário já ocupado.', { icon: 'ℹ️' }) : null}
                                        disabled={!podeClicar}
                                        className={`text-xs font-medium px-2 py-2 rounded-lg flex items-center justify-center gap-1 ${
                                          slot.ocupado
                                            ? slot.status === 'PENDENTE'
                                              ? 'bg-yellow-100 text-yellow-800 cursor-not-allowed'
                                              : 'bg-red-100 text-red-700 cursor-not-allowed'
                                            : podeClicar
                                              ? 'bg-green-100 text-green-700 active:bg-green-200'
                                              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                                        }`}
                                      >
                                        <span className={`w-2 h-2 rounded-full ${slot.ocupado ? (slot.status === 'PENDENTE' ? 'bg-yellow-500' : 'bg-red-500') : 'bg-green-500'}`}></span>
                                        {slot.ocupado ? (slot.status === 'PENDENTE' ? 'Em análise' : slot.empresa || 'Agendado') : horario}
                                      </button>
                                    );
                                  })}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        <div className="flex flex-wrap items-center gap-3 mt-4 pt-3 border-t border-gray-100 text-xs text-gray-600">
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-full bg-green-500"></span>
                            <span>Horário Disponível</span>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="w-3 h-3 rounded-full bg-red-500"></span>
                            <span>Horário Agendado</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center h-full flex flex-col items-center justify-center">
                    <svg className="w-12 h-12 text-gray-300 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-gray-600">Selecione um comprador para visualizar os horários disponíveis.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {abaAtiva === 'minhas' && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">Minhas Solicitações</h2>
            </div>

            <div className="p-4 border-b border-gray-200 bg-gray-50">
              <p className="text-sm font-medium text-gray-700 mb-2">Filtrar por status</p>
              <div className="flex flex-wrap gap-2">
                {(Object.keys(statusConfig) as StatusVisita[]).map(status => {
                  const selecionado = statusFiltro.includes(status);
                  return (
                    <button
                      key={status}
                      type="button"
                      onClick={() => toggleStatusFiltro(status)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                        selecionado
                          ? statusConfig[status].cor
                          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      <span className="flex items-center gap-1.5">
                        <span className={`w-3 h-3 rounded-sm border ${selecionado ? 'bg-current' : 'bg-transparent border-gray-400'}`} />
                        {statusConfig[status].label}
                      </span>
                    </button>
                  );
                })}
              </div>
              {statusFiltro.length > 0 && (
                <button
                  onClick={() => setStatusFiltro([])}
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700 underline"
                >
                  Limpar filtros
                </button>
              )}
            </div>

            <div className="divide-y divide-gray-100">
              {minhasVisitas.filter(v => statusFiltro.length === 0 || statusFiltro.includes(v.status)).length === 0 ? (
                <div className="p-8 text-center text-gray-500">Nenhuma solicitação encontrada.</div>
              ) : (
                minhasVisitas.filter(v => statusFiltro.length === 0 || statusFiltro.includes(v.status)).map(visita => (
                  <div key={visita.id} className="p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium border ${statusConfig[visita.status].cor}`}>
                            {statusConfig[visita.status].label}
                          </span>
                          <span className="text-sm text-gray-500">{visita.codigo}</span>
                        </div>
                        <p className="font-medium text-gray-900">{visita.assunto}</p>
                        <p className="text-sm text-gray-600">
                          Comprador: {visita.comprador?.nome} | {new Date(visita.data_visita + 'T12:00:00').toLocaleDateString('pt-BR')} | {visita.horario_inicio} - {visita.horario_fim}
                        </p>
                        {visita.motivo_cancelamento && (
                          <p className="text-sm text-red-600 mt-1">
                            <strong>Motivo do cancelamento:</strong> {visita.motivo_cancelamento}
                          </p>
                        )}
                        <div className="mt-3">
                          {renderFluxograma(visita.status)}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {visita.status === 'PENDENTE' && (
                          <button
                            onClick={() => handleReagendarClick(visita)}
                            className="px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100"
                          >
                            Reagendar
                          </button>
                        )}
                        {['PENDENTE', 'APROVADO', 'REAGENDADO'].includes(visita.status) && (
                          <button
                            onClick={() => handleCancelarVisita(visita)}
                            className="px-3 py-1.5 text-sm font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100"
                          >
                            Cancelar
                          </button>
                        )}
                        {visita.status === 'REAGENDADO' && (
                          <>
                            <button
                              onClick={() => handleAprovar(visita)}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700"
                            >
                              Aprovar
                            </button>
                            <button
                              onClick={() => handleReprovar(visita)}
                              className="px-3 py-1.5 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700"
                            >
                              Reprovar
                            </button>
                          </>
                        )}
                        {['APROVADO', 'CHEGOU', 'EM_ATENDIMENTO', 'FINALIZADO'].includes(visita.status) && (
                          <button
                            onClick={() => handleComprovante(visita)}
                            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                          >
                            Comprovante
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </div>

      {modalAberto && compradorSelecionado && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-2 sm:p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-[calc(100vw-1rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-3 sm:p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-semibold text-gray-900 truncate pr-2">
                {etapaModal === 'horario' ? 'Selecionar Horário' : 'Solicitar Visita'}
              </h3>
              <button onClick={() => setModalAberto(false)} className="text-gray-400 hover:text-gray-600 flex-shrink-0">
                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {etapaModal === 'horario' ? (
              <div className="p-4 sm:p-6">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4">
                  <p className="text-blue-800 text-sm truncate">
                    <strong>Comprador:</strong> {compradorSelecionado.nome}
                  </p>
                  <p className="text-blue-800 text-sm">
                    <strong>Data:</strong> {diasSemanaCompleto[new Date(dataSelecionada + 'T12:00:00').getDay()]}, {new Date(dataSelecionada + 'T12:00:00').toLocaleDateString('pt-BR')}
                  </p>
                </div>

                {carregando ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : fechado ? (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
                    <p className="text-red-700 font-medium">Dia/horário indisponível</p>
                    <p className="text-red-600 text-sm mt-1">{motivoFechado}</p>
                  </div>
                ) : slots.length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
                    <p className="text-yellow-700">Nenhum horário disponível para esta data.</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 sm:gap-3">
                    {slots.map((slot, index) => {
                      const ocupado = slot.ocupado;
                      const podeEscolher = podeSolicitar && !ocupado;
                      return (
                        <button
                          key={index}
                          onClick={() => podeEscolher ? handleSelecionarSlot(slot) : ocupado ? toast('Horário já ocupado.', { icon: 'ℹ️' }) : toast.error('Você não tem permissão para solicitar visitas.')}
                          disabled={!podeEscolher}
                          className={`p-2 sm:p-3 rounded-lg border text-xs sm:text-sm font-medium transition-colors ${
                            ocupado
                              ? 'border-red-200 bg-red-50 text-red-700 cursor-not-allowed'
                              : podeSolicitar
                                ? 'border-green-200 bg-green-50 text-green-700 hover:bg-green-100'
                                : 'border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed'
                          }`}
                        >
                          {slot.inicio} - {slot.fim}
                          {ocupado && <span className="block text-xs font-normal mt-1">Ocupado</span>}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="p-4 sm:p-6 space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 mb-4">
                  <p className="text-blue-800 text-sm truncate">
                    <strong>Comprador:</strong> {compradorSelecionado.nome}
                  </p>
                  <p className="text-blue-800 text-sm">
                    <strong>Data:</strong> {new Date(dataSelecionada + 'T12:00:00').toLocaleDateString('pt-BR')} | <strong>Horário:</strong> {slotSelecionado?.inicio} - {slotSelecionado?.fim}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Assunto da Visita *</label>
                  <input
                    required
                    type="text"
                    className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={formVisita.assunto}
                    onChange={(e) => setFormVisita({ ...formVisita, assunto: e.target.value })}
                    placeholder="Ex: Apresentação de nova linha de produtos"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Documento (RG ou CPF) *</label>
                    <input
                      required
                      type="text"
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formVisita.documento}
                      onChange={(e) => setFormVisita({ ...formVisita, documento: e.target.value })}
                      placeholder="000.000.000-00"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Empresa Representada *</label>
                    <input
                      required
                      type="text"
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formVisita.empresa_representada}
                      onChange={(e) => setFormVisita({ ...formVisita, empresa_representada: e.target.value })}
                      placeholder="Nome da empresa"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nome do Visitante</label>
                    <input
                      type="text"
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formVisita.nome_visitante}
                      onChange={(e) => setFormVisita({ ...formVisita, nome_visitante: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      type="email"
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formVisita.email_visitante}
                      onChange={(e) => setFormVisita({ ...formVisita, email_visitante: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Telefone</label>
                    <input
                      type="text"
                      className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                      value={formVisita.telefone_visitante}
                      onChange={(e) => setFormVisita({ ...formVisita, telefone_visitante: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Observações</label>
                  <textarea
                    rows={3}
                    className="w-full rounded-lg border-gray-300 focus:border-blue-500 focus:ring-blue-500"
                    value={formVisita.observacoes}
                    onChange={(e) => setFormVisita({ ...formVisita, observacoes: e.target.value })}
                  />
                </div>

                <SelfieCapture onCapture={setFotoVisitanteFile} disabled={enviandoSolicitacao} />

                <div className="flex flex-col sm:flex-row justify-between gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setEtapaModal('horario')}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                  >
                    Voltar
                  </button>
                  <div className="flex gap-3 justify-end">
                    <button
                      type="button"
                      onClick={() => setModalAberto(false)}
                      className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      disabled={enviandoSolicitacao}
                      className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {enviandoSolicitacao ? 'Enviando...' : 'Solicitar Visita'}
                    </button>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

    </div>
  );
};

export default AgendarVisitas;
